import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { classNames } from "../../lib/format";
import type {
  GeocodeResponse,
  GeocodeResult,
  ReverseGeocodeResponse,
} from "../../types/routecore";
import { PageHeader } from "../PageHeader";
import { SectionCard } from "../SectionCard";

// Tab de exemplo: consumo de TILES VETORIAIS (.pbf, schema OpenMapTiles) com
// MapLibre GL JS. Diferente da aba "Mapa" (raster, via Leaflet), aqui o
// browser baixa a geometria vetorial e rasteriza o mapa na GPU a partir de um
// `style.json`. Ganhos didáticos: troca de tema claro/escuro sem rebaixar
// tiles, rótulos nítidos em qualquer zoom e rotação/inclinação nativas.

// Prefixo de mesma origem por onde TODOS os recursos do tileserver passam
// (dev: proxy do Vite; prod: nginx do frontend) — ver vite.config.ts e
// kitchen-sink/frontend/nginx.conf.
const TILE_BASE = "/tiles";

// Estilos MapLibre completos servidos pelo TileServer GL. O style CLARO pode
// ser sobrescrito por env (mesmo padrão do TabMap raster: `||`, não `??`,
// porque o build-arg do Docker pode chegar como string vazia). O dado
// vetorial (source `v3`) é o MESMO nos dois estilos — muda só a folha de
// pintura, então alternar tema é um simples setStyle (ver switchTheme).
const STYLE_LIGHT_URL =
  (import.meta.env.VITE_TILESERVER_STYLE_URL as string | undefined) ||
  `${TILE_BASE}/styles/liberty/style.json`;
const STYLE_DARK_URL = `${TILE_BASE}/styles/dark/style.json`;

// Centro inicial: Florianópolis/SC — o dataset atual cobre Santa Catarina.
// Atenção: MapLibre usa [lng, lat] (ordem GeoJSON), ao contrário do Leaflet.
const INITIAL_CENTER: [number, number] = [-48.55, -27.59];
const INITIAL_ZOOM = 11;

// Atribuição obrigatória pela licença ODbL do OpenStreetMap.
const OSM_ATTRIBUTION = "© OpenStreetMap contributors";

// Cor do pino de resultado da busca (brand-600 do tema — ver tailwind.config).
// maplibregl.Marker aceita a cor como string CSS e desenha o SVG padrão.
const MARKER_COLOR = "#16a34a";

// Exemplo de linha de endereço concatenada (logradouro - direcional, número,
// bairro, município - UF) — dispara o pré-tratamento determinístico do BFF.
const SEARCH_PLACEHOLDER = "Rua Marechal Bormann - D, 559, Centro, Chapecó - SC";

// Detecta um par "lat, lon" digitado direto na busca: ponto decimal, com
// vírgula, ponto-e-vírgula ou espaço como separador. Casar aqui desvia do
// /api/geocode — o texto é tratado como coordenadas, não como endereço.
const COORD_RE = /^\s*-?\d{1,2}(\.\d+)?\s*[,;\s]\s*-?\d{1,3}(\.\d+)?\s*$/;

// Namespaces que o TileServer GL injeta no style.json como URL ABSOLUTA
// baseada no Host do request (ex.: http://<host>/data/v3.json, /fonts/…,
// /sprites/…, /styles/…) — SEM o nosso prefixo /tiles. Atrás do proxy o
// MapLibre buscaria esses paths na origem do app e cairia no index.html.
const PROXIED_PREFIXES = ["/data/", "/fonts/", "/sprites/", "/styles/"];

// transformRequest reancora cada requisição do MapLibre no prefixo /tiles
// (mesma origem). Aproveita só o pathname — ignora host E scheme que o
// tileserver reescreveu (atrás do proxy ele chega a emitir http://) — e
// recompõe uma URL ABSOLUTA em window.location.origin: os tiles .pbf são
// buscados pelo web worker do MapLibre (que roda de um blob:), onde URL
// relativa não resolve. A requisição do próprio style.json (já sob
// /tiles/styles/…) NÃO casa com os prefixos acima, então passa intacta e
// não vira /tiles/tiles.
const transformRequest: maplibregl.RequestTransformFunction = (url) => {
  let parsed: URL;
  try {
    // A base cobre URLs relativas; para as absolutas do tileserver, é ignorada.
    parsed = new URL(url, window.location.origin);
  } catch {
    return undefined; // URL malformada — deixa o MapLibre seguir com o default.
  }
  const { pathname, search } = parsed;
  if (!PROXIED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return undefined;
  return { url: `${window.location.origin}${TILE_BASE}${pathname}${search}` };
};

// shortAddress reduz o display_name do Nominatim (que pode ter muitos
// componentes) aos 3 primeiros — o suficiente para o popup do pino.
function shortAddress(formatted: string): string {
  const parts = formatted.split(",").map((p) => p.trim()).filter(Boolean);
  return parts.slice(0, 3).join(", ") || formatted;
}

export function TabMapVector() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">("light");

  // Estado da busca de geocoding (barra acima do mapa).
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [meta, setMeta] = useState<{ precision?: string; matchedCandidate?: string } | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Ponto pendente vindo de outra aba (ex.: "Abrir no Mapa" da aba Geocoding).
  // O ref espelha o valor atual para o effect de criação (deps []) enxergar o
  // ponto já presente no MOUNT — o caso típico: a navegação monta esta aba
  // com o ponto à espera.
  const { pendingMapPoint, setPendingMapPoint } = useAppState();
  const pendingRef = useRef(pendingMapPoint);
  pendingRef.current = pendingMapPoint;

  // Cria o mapa UMA vez (mount) e o destrói no unmount. O estilo inicial é o
  // claro; a troca de tema é imperativa (setStyle) no handler abaixo, sem
  // recriar o mapa. transformRequest é opção do Map e persiste entre setStyle.
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_LIGHT_URL,
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      // Injeta a atribuição do OSM no controle padrão (obrigação da ODbL).
      attributionControl: { compact: true, customAttribution: OSM_ATTRIBUTION },
      transformRequest,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    mapRef.current = map;
    // Consome o ponto pendente do mount IMEDIATAMENTE: Marker é overlay DOM e
    // flyTo é operação de câmera — ambos independem do carregamento do estilo,
    // então não há o que esperar. (Aguardar load/loaded() é frágil: loaded()
    // segue false com tiles em voo mesmo após o evento load, e um once("load")
    // registrado tarde nunca dispara.)
    const p = pendingRef.current;
    if (p) {
      const marker = new maplibregl.Marker({ color: MARKER_COLOR })
        .setLngLat([p.lon, p.lat])
        .setPopup(new maplibregl.Popup({ offset: 24 }).setText(shortAddress(p.label)))
        .addTo(map);
      marker.togglePopup();
      markerRef.current = marker;
      map.flyTo({ center: [p.lon, p.lat], zoom: 16 });
      setPendingMapPoint(null);
    }
    return () => {
      // Marker é overlay DOM próprio — removê-lo explicitamente junto do mapa.
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; o ponto do mount chega via pendingRef
  }, []);

  // Alterna claro/escuro: só um setStyle — a geometria vetorial (source v3) é
  // a mesma, muda apenas o style.json. O estado local serve para a UI.
  function switchTheme(next: "light" | "dark") {
    if (next === theme) return;
    setTheme(next);
    mapRef.current?.setStyle(next === "dark" ? STYLE_DARK_URL : STYLE_LIGHT_URL);
  }

  // Posiciona (ou reposiciona) o pino em lat/lon, abre o popup com o texto dado
  // e voa até ele (zoom 16). Devolve o Marker para quem precise atualizar o
  // popup depois (ex.: o reverse-geocode do modo coordenadas). O Marker é um
  // overlay DOM gerenciado pelo Map: sobrevive ao setStyle do toggle de tema
  // (só as camadas WebGL são recriadas), então não é recriado ao alternar
  // claro/escuro.
  function placePin(lat: number, lon: number, popupText: string): maplibregl.Marker | null {
    const map = mapRef.current;
    if (!map) return null;
    markerRef.current?.remove(); // nova busca/seleção substitui o pino anterior
    const center: [number, number] = [lon, lat];
    const marker = new maplibregl.Marker({ color: MARKER_COLOR })
      .setLngLat(center)
      .setPopup(new maplibregl.Popup({ offset: 24 }).setText(popupText))
      .addTo(map);
    marker.togglePopup(); // já abre o popup para dar contexto do ponto
    markerRef.current = marker;
    map.flyTo({ center, zoom: 16 });
    return marker;
  }

  function showOnMap(r: GeocodeResult) {
    placePin(r.location.lat, r.location.lon, shortAddress(r.formatted_address));
  }

  function selectResult(i: number) {
    setActiveIdx(i);
    showOnMap(results[i]);
  }

  // Modo coordenadas: posiciona o pino direto no par "lat, lon" digitado, sem
  // passar pelo /api/geocode. Valida a faixa e, como bônus didático, tenta
  // enriquecer o popup com o endereço via /api/reverse-geocode — mesma chamada
  // da aba Geocoding. Falha do reverse é silenciosa (popup fica só com coords).
  async function showCoords(raw: string) {
    const [lat, lon] = raw.split(/[\s,;]+/).map(Number);
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      setError("Coordenadas fora de faixa. Confira a ordem: lat (-90 a 90), lon (-180 a 180).");
      return;
    }
    // Modo coordenadas não lista resultados — limpa o estado da busca por endereço.
    setError(null);
    setResults([]);
    setMeta(null);
    const coordText = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    const marker = placePin(lat, lon, coordText);
    if (!marker) return;
    const r = (await api.reverseGeocode({ lat, lon })) as APIResult<ReverseGeocodeResponse>;
    // Só enriquece se o pino ainda é este (o usuário pode ter buscado de novo).
    if (r.ok && r.data?.formatted_address && markerRef.current === marker) {
      marker.getPopup()?.setText(`${shortAddress(r.data.formatted_address)} · ${coordText}`);
    }
  }

  // Consome um ponto setado com a aba JÁ montada (caso raro — o fluxo normal
  // do "Abrir no Mapa" monta a aba e é consumido no effect de criação acima).
  // Posiciona direto: marker e flyTo independem do carregamento do estilo.
  useEffect(() => {
    const point = pendingMapPoint;
    if (!point) return;
    if (!mapRef.current) return; // sem mapa ainda: o effect de criação consome
    placePin(point.lat, point.lon, shortAddress(point.label));
    setPendingMapPoint(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- placePin é estável por render e usa refs
  }, [pendingMapPoint, setPendingMapPoint]);

  // Geocodifica a linha digitada via BFF (/api/geocode) — country_code fixo em
  // BR e limit 5, como na aba Geocoding. O BFF aplica o pré-tratamento de linha
  // antes de repassar ao upstream; erros ficam inline (sem alert, que travaria
  // a automação do browser).
  async function runSearch() {
    const address = query.trim();
    if (!address || searching) return;
    // Se o texto é um par "lat, lon", desvia para o modo coordenadas (não toca
    // no /api/geocode): posiciona direto e valida a faixa.
    if (COORD_RE.test(address)) {
      showCoords(address);
      return;
    }
    setSearching(true);
    setError(null);
    const r = (await api.geocode({
      address,
      country_code: "BR",
      limit: 5,
    })) as APIResult<GeocodeResponse>;
    setSearching(false);
    if (!r.ok || !r.data) {
      setError(`Falha na busca (HTTP ${r.status}).`);
      setResults([]);
      setMeta(null);
      return;
    }
    const hits = r.data.results ?? [];
    if (hits.length === 0) {
      setError("Nenhum resultado para este endereço.");
      setResults([]);
      setMeta(null);
      return;
    }
    // Metadados do pré-tratamento só chegam quando o pipeline atuou (nesse
    // caso o upstream devolve um único resultado); no passthrough vêm ausentes.
    setMeta(
      r.data.matched_candidate || r.data.precision
        ? { precision: r.data.precision, matchedCandidate: r.data.matched_candidate }
        : null,
    );
    setResults(hits);
    setActiveIdx(0);
    showOnMap(hits[0]);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mapa vetorial (MapLibre GL)"
        subtitle="Exemplo de consumo dos tiles vetoriais (.pbf, schema OpenMapTiles) e dos estilos MapLibre servidos pelo TileServer GL, com renderização no cliente via WebGL."
      />

      <SectionCard
        title="Estilo"
        description="O mesmo dado vetorial renderizado com dois estilos MapLibre. Alternar tema é um setStyle — nenhum tile é baixado de novo."
        actions={
          <div className="flex gap-1 rounded-md border border-ink-300 bg-white p-0.5">
            {(["light", "dark"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => switchTheme(t)}
                className={classNames(
                  "rounded px-2.5 py-1 text-xs font-medium transition",
                  theme === t
                    ? "bg-brand-600 text-white"
                    : "text-ink-600 hover:bg-ink-100",
                )}
              >
                {t === "light" ? "Claro" : "Escuro"}
              </button>
            ))}
          </div>
        }
      >
        <p className="text-[11px] leading-relaxed text-ink-500">
          Recursos servidos por <code className="ks-code">{STYLE_LIGHT_URL}</code> e{" "}
          <code className="ks-code">{STYLE_DARK_URL}</code>, sempre via proxy de mesma origem{" "}
          (<code className="ks-code">/tiles</code>). Um{" "}
          <code className="ks-code">transformRequest</code> reancora nesse prefixo as URLs
          absolutas que o TileServer GL injeta no <code className="ks-code">style.json</code>{" "}
          (data/fonts/sprites/styles). Sobrescreva o estilo claro com{" "}
          <code className="ks-code">VITE_TILESERVER_STYLE_URL</code> se precisar de outro.
        </p>
      </SectionCard>

      <SectionCard
        title="Busca de endereço ou coordenadas"
        description='Geocodifica uma linha de endereço concatenada e posiciona o resultado no mapa. Reusa POST /api/geocode (mesmo endpoint da aba Geocoding), que aplica o pré-tratamento determinístico do BFF quando a linha é estruturada. Também aceita um par lat, lon (ex.: "-27.1009, -52.6121"): nesse caso posiciona direto no mapa, sem geocode.'
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
          className="flex flex-col gap-2 sm:flex-row"
        >
          <input
            type="text"
            className="ks-input flex-1"
            placeholder={SEARCH_PLACEHOLDER}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="submit"
            className="ks-btn-primary text-sm sm:w-32"
            disabled={searching || !query.trim()}
          >
            {searching ? "buscando..." : "Buscar"}
          </button>
        </form>

        {error && (
          <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {error}
          </p>
        )}

        {results.length > 0 && (
          <ul className="mt-3 space-y-1">
            {results.map((r, i) => (
              <li key={`${r.location.lat}-${r.location.lon}-${i}`}>
                <button
                  type="button"
                  onClick={() => selectResult(i)}
                  className={classNames(
                    "flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition",
                    i === activeIdx
                      ? "border-brand-300 bg-brand-50"
                      : "border-ink-200 bg-white hover:bg-ink-50",
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-ink-900">{r.formatted_address}</p>
                    <p className="text-[11px] font-mono text-ink-500">
                      {r.location.lat.toFixed(5)}, {r.location.lon.toFixed(5)}
                      {r.place_type ? ` · ${r.place_type}` : ""}
                    </p>
                  </div>
                  {i === 0 && meta && (meta.precision || meta.matchedCandidate) && (
                    <span className="ks-tag shrink-0 bg-brand-100 text-brand-800">
                      {[meta.precision, meta.matchedCandidate].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <div className="ks-card overflow-hidden">
        <div ref={containerRef} className="h-[60vh] min-h-[420px] w-full" />
      </div>
    </div>
  );
}
